import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Zap,
  ArrowRight,
  AlertCircle,
  ShieldCheck,
  Lock,
  Cpu,
  Key,
} from "lucide-react";
import { useAuthStore } from "../store/authStore";

const Login = () => {
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(false);
  const [authError, setAuthError] = useState(null);
  const { isAuthenticated, checkAuth } = useAuthStore();
  const navigate = useNavigate();

  const backendUrl = import.meta.env.VITE_BACKEND_URL || "http://localhost:3001";

  useEffect(() => {
    // Check if user is already authenticated
    checkAuth().then((user) => {
      if (user) {
        navigate("/dashboard", { replace: true });
      }
    });

    // Check query params for error
    const errorParam = searchParams.get("error");
    if (errorParam) {
      if (errorParam === "auth_failed") {
        setAuthError("OAuth authentication failed. Please try signing in again.");
      } else if (errorParam === "missing_code") {
        setAuthError("Authorization code was missing from OAuth response.");
      } else {
        setAuthError(`Authentication error: ${errorParam}`);
      }
    }
  }, [searchParams, navigate, checkAuth]);

  const handleSSOLogin = () => {
    setIsLoading(true);
    setAuthError(null);
    window.location.href = `${backendUrl}/api/auth/login`;
  };

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Left Panel - Branding */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden bg-slate-100">
        {/* Background Effects */}
        <div className="absolute inset-0 bg-gradient-to-br from-white via-slate-50 to-slate-100" />
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary-200/50 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-accent-200/50 rounded-full blur-3xl" />

        {/* Grid Pattern */}
        <div className="absolute inset-0 opacity-50">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `linear-gradient(rgba(203, 213, 225, 0.5) 1px, transparent 1px),
                               linear-gradient(90deg, rgba(203, 213, 225, 0.5) 1px, transparent 1px)`,
              backgroundSize: "50px 50px",
            }}
          />
        </div>

        {/* Content */}
        <div className="relative z-10 flex flex-col justify-center px-12 xl:px-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="flex items-center gap-3 mb-8">
              <img
                src="/Machine-Interface-dark.png"
                alt="ConvoLink Logo"
                className="h-16 w-auto"
              />
            </div>

            <h1 className="text-4xl xl:text-5xl font-bold text-slate-900 leading-tight mb-4">
              AI-Powered Industrial Operations
            </h1>
            <p className="text-lg text-slate-600 mb-8 max-w-lg">
              Intelligent machine operations, Standard Operating Procedures, and
              predictive maintenance unified by enterprise Single Sign-On.
            </p>

            <div className="space-y-4">
              {[
                {
                  icon: Zap,
                  title: "Smart Machine Guidance",
                  desc: "AI conversational assistant with contextual telemetry",
                },
                {
                  icon: ShieldCheck,
                  title: "Enterprise SSO & RBAC",
                  desc: "Centralized identity and fine-grained role permissions",
                },
                {
                  icon: Cpu,
                  title: "Predictive Maintenance",
                  desc: "Real-time anomaly detection and failure forecasts",
                },
              ].map((item, idx) => (
                <div key={idx} className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl bg-primary-100 flex items-center justify-center flex-shrink-0">
                    <item.icon className="w-5 h-5 text-primary-600" />
                  </div>
                  <div>
                    <h2 className="font-semibold text-slate-900">
                      {item.title}
                    </h2>
                    <p className="text-sm text-slate-500">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>

      {/* Right Panel - SSO Action */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6 }}
          className="w-full max-w-md"
        >
          {/* Mobile Logo */}
          <div className="mb-8 lg:hidden">
            <img
              src="/Machine-Interface-dark.png"
              alt="ConvoLink Logo"
              className="h-12 w-auto"
            />
          </div>

          <div className="bg-white p-8 rounded-2xl shadow-xl shadow-slate-200/60 border border-slate-100">
            <div className="mb-8">
              <div className="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center text-blue-600 mb-4">
                <Key className="w-6 h-6" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900 mb-2">
                Enterprise Single Sign-On
              </h2>
              <p className="text-slate-500 text-sm">
                Authenticate securely through the organization's Central OAuth Identity Provider
              </p>
            </div>

            {authError && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-center gap-3 text-red-700"
              >
                <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
                <p className="text-sm">{authError}</p>
              </motion.div>
            )}

            <div className="space-y-5">
              <div className="p-4 bg-slate-50 border border-slate-200/80 rounded-xl space-y-2">
                <div className="flex items-center gap-2 text-xs font-semibold text-slate-700 uppercase tracking-wider">
                  <ShieldCheck className="w-4 h-4 text-emerald-600" />
                  Secure BFF Session
                </div>
                <p className="text-xs text-slate-500 leading-relaxed">
                  Credentials and access tokens are managed exclusively through secure, server-side HTTP-only cookies and Session Service.
                </p>
              </div>

              <button
                type="button"
                onClick={handleSSOLogin}
                disabled={isLoading}
                className="w-full flex items-center justify-center gap-2 py-3 px-4 bg-blue-600 hover:bg-blue-700 active:bg-blue-800 text-white font-medium rounded-xl shadow-lg shadow-blue-500/25 transition disabled:opacity-50"
              >
                {isLoading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    <span>Connecting to OAuth Server...</span>
                  </>
                ) : (
                  <>
                    <span>Sign in with Central SSO</span>
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>
            </div>

            <div className="mt-8 pt-6 border-t border-slate-100 flex items-center justify-between text-xs text-slate-400">
              <span className="flex items-center gap-1">
                <Lock className="w-3.5 h-3.5" /> 256-bit Encrypted
              </span>
              <span>ConvoLink v1.0</span>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Login;